import { inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { decodeJwt } from 'jose';
import { AuthApiService } from './api/auth-api.service';

@Injectable({
  providedIn: 'root',
})
export class AccessTokenService {
  private readonly authApi = inject(AuthApiService);

  private accessToken?: string;
  private tokenValidUntil?: number;
  private tokenType?: string;
  private currentLoadRequest?: Promise<string>;

  private readonly sessionExpired = new Subject<void>();
  /**
   * Emits when the backend refuses to renew the session (refresh token expired, revoked, or the user is gone).
   * Network and server errors don't count: those say nothing about whether the session is still valid.
   */
  public readonly sessionExpired$ = this.sessionExpired.asObservable();

  public set(token: string) {
    const decoded = decodeJwt(token);
    this.tokenValidUntil = decoded.exp ? decoded.exp * 1000 : undefined;
    this.tokenType = typeof decoded['type'] === 'string' ? decoded['type'] : undefined;
    this.accessToken = token;
  }

  public clear() {
    this.tokenValidUntil = undefined;
    this.tokenType = undefined;
    this.accessToken = undefined;
  }

  /**
   * The backend issues a `CLIENT`-type token only for accounts with no staff/therapist role
   * (see JwtRequestFilter.kt, which grants ROLE_STAFF vs ROLE_CLIENT based on this same claim).
   * This app only ever logs in and refreshes through the client endpoints, which never hand out
   * anything else - this check makes sure a staff token can't unlock the game if that ever changes.
   */
  public isClientToken(): boolean {
    return this.tokenType === 'CLIENT';
  }

  public get(): Promise<string> {
    // Check if the token is still valid for the next 30 seconds, if it is, return/use it
    if (this.accessToken !== undefined && (this.tokenValidUntil === undefined || this.tokenValidUntil > Date.now() + 30 * 1000)) {
      return Promise.resolve(this.accessToken);
    }

    // We reset the token to prevent reusing an expired token
    this.clear();

    // Concurrent callers share a single refresh, since the backend rotates the refresh token on every use. Once it
    // settles (either way) the next call starts a fresh attempt, so one failed refresh can't block every later request.
    this.currentLoadRequest ??= this.loadNewToken().finally(() => (this.currentLoadRequest = undefined));
    return this.currentLoadRequest;
  }

  private async loadNewToken() {
    const response = await this.refreshAccessToken();
    if (response.isErr()) {
      const error = response.unwrapErr();
      switch (error.code) {
        case 'INVALID_REFRESH_TOKEN':
        case 'RESOURCE_NOT_FOUND':
          this.sessionExpired.next();
          break;
      }

      throw error;
    }

    const { accessToken } = response.unwrap();
    this.set(accessToken);
    return accessToken;
  }

  /**
   * All tabs send the same refresh token cookie, and the backend rotates it on every refresh. A tab refreshing while
   * another one is mid-rotation would present the token that is being rotated away, get rejected and log everyone out.
   * The lock makes tabs take turns, so each refresh goes out with the cookie the previous one received.
   */
  private refreshAccessToken(): ReturnType<AuthApiService['refreshAccessToken']> {
    if (!('locks' in navigator)) return this.authApi.refreshAccessToken();
    return navigator.locks.request('refresh-access-token', () => this.authApi.refreshAccessToken());
  }
}
